import { enforceShape } from "@/src/lib/enforce-shape";
import { logger, logAndThrow } from "@/src/lib/logging/logger";
import { withDurationMetric } from "@/src/lib/metrics";
import { withRetry } from "@/src/lib/retry";
import { customCamelCase, customSnakeCase } from "@/src/lib/string";
import { getSupabaseClient } from "@/src/lib/supabase";
import { AppointmentRow } from "@/src/lib/types/tables.types";
import { CSV_EXPORT_MAX_ROWS } from "@/src/lib/utils/csv";
import {
  Appointment,
  AppointmentCreatedBy,
  AppointmentIntegrationNamespace,
  AppointmentMetadata,
  AppointmentMetadataFiltersParams,
  EhrAppointmentIdNamespace,
  PaginatedAppointmentsMetadataResponse,
  appointmentIntegrationMetadataSchema,
  appointmentShape,
} from "@/src/models/appointment";
import { Group } from "@/src/models/group";
import { BaseRepository } from "@/src/repo/base-repository";

export const APPOINTMENT_TABLE_NAME = "appointment";
export const fullAppointmentSelect = "*";

const APPOINTMENT_LIST_SORT_FIELDS: Record<string, string> = {
  appointmentCreatedAt: "appointment_created_at",
  appointmentDatetime: "appointment_datetime",
  createdAt: "created_at",
  createdBy: "created_by",
  displayId: "display_id",
  ehrStatus: "ehr_status",
  ehrType: "ehr_type",
  status: "status",
  type: "type",
  updatedAt: "updated_at",
};

function resolveAppointmentListSortField(sortBy: string): string {
  return APPOINTMENT_LIST_SORT_FIELDS[sortBy] ?? "created_at";
}

/**
 * Maps an appointment row from the database to an Appointment model object
 * Handles fields like appointmentCreatedAt, appointmentDatetime, type, status, and patientId
 */
export function mapAppointmentRowToAppointment(appointmentRow: any): Appointment {
  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(appointmentRow)) {
    mappedFields[customCamelCase(key)] = value;
  }

  if (mappedFields.integrationMetadata != null) {
    mappedFields.integrationMetadata = appointmentIntegrationMetadataSchema.parse(mappedFields.integrationMetadata);
  }

  return mappedFields as Appointment;
}

/**
 * Maps an Appointment model object to a database row format
 * Handles all fields including type, status, and patientId
 */
function mapAppointmentToAppointmentRow(appointment: Appointment): AppointmentRow {
  const cleanedAppointment = enforceShape(appointment, appointmentShape);

  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(cleanedAppointment)) {
    mappedFields[customSnakeCase(key)] = value;
  }

  return mappedFields as AppointmentRow;
}

export class AppointmentRepository extends BaseRepository<Appointment, AppointmentRow> {
  constructor() {
    super({
      tableName: APPOINTMENT_TABLE_NAME,
      selectClause: fullAppointmentSelect,
      mapRowToModel: mapAppointmentRowToAppointment,
      mapModelToRow: mapAppointmentToAppointmentRow,
    });
  }
}

export const appointmentRepository = new AppointmentRepository();
export const getAppointment = appointmentRepository.get.bind(appointmentRepository);
export const insertAppointment = appointmentRepository.insert.bind(appointmentRepository);
export const updateAppointment = appointmentRepository.update.bind(appointmentRepository);
export const deleteAppointment = appointmentRepository.delete.bind(appointmentRepository);

export const getAppointmentByEhrId = withDurationMetric(async function getAppointmentByEhrId(
  ehrId: string,
  group: Group
): Promise<Appointment | null> {
  if (!ehrId || !group) {
    return null;
  }

  const { data: appointmentRows, error } = await withRetry("getAppointmentByEhrId", async () => {
    const supabaseClient = await getSupabaseClient();

    return await supabaseClient
      .from(APPOINTMENT_TABLE_NAME)
      .select(fullAppointmentSelect)
      .eq("group_id", group.id)
      .eq("ehr_id", ehrId)
      .is("deleted_at", null)
      .limit(1);
  });

  if (error) {
    logger.error("Error getting appointment by ehrId", { ehrId, error });
    throw new Error(`Error getting appointment by ehrId: ${ehrId}`);
  }

  if (!appointmentRows || appointmentRows.length === 0) {
    return null;
  }

  const appointmentToReturn = mapAppointmentRowToAppointment(appointmentRows[0]);

  return appointmentToReturn;
}, "getAppointmentByEhrId");

export const getAppointmentByEhrIdentifier = withDurationMetric(async function getAppointmentByEhrIdentifier(
  ehrId: string,
  namespace: EhrAppointmentIdNamespace,
  group: Group
): Promise<Appointment | null> {
  if (!ehrId || !group) {
    return null;
  }

  const { data: appointmentRows, error } = await withRetry("getAppointmentByEhrIdentifier", async () => {
    const supabaseClient = await getSupabaseClient();

    return await supabaseClient
      .from(APPOINTMENT_TABLE_NAME)
      .select(fullAppointmentSelect)
      .eq("group_id", group.id)
      .eq("ehr_id_namespace", namespace)
      .eq("ehr_id", ehrId)
      .is("deleted_at", null)
      .limit(1);
  });

  if (error) {
    logAndThrow("Error getting appointment by namespaced EHR identifier", { namespace, error });
  }

  return appointmentRows?.[0] ? mapAppointmentRowToAppointment(appointmentRows[0]) : null;
}, "getAppointmentByEhrIdentifier");

export const getAppointmentByIntegrationIdentifier = withDurationMetric(
  async function getAppointmentByIntegrationIdentifier(
    integrationAppointmentId: string,
    namespace: AppointmentIntegrationNamespace,
    group: Group
  ): Promise<Appointment | null> {
    if (!integrationAppointmentId || !group) {
      return null;
    }

    const { data: appointmentRows, error } = await withRetry("getAppointmentByIntegrationIdentifier", async () => {
      const supabaseClient = await getSupabaseClient();

      return await supabaseClient
        .from(APPOINTMENT_TABLE_NAME)
        .select(fullAppointmentSelect)
        .eq("group_id", group.id)
        .eq("integration_namespace", namespace)
        .eq("integration_appointment_id", integrationAppointmentId)
        .is("deleted_at", null)
        .limit(2);
    });

    if (error) {
      logAndThrow("Error getting appointment by namespaced integration identifier", { namespace, error });
    }

    if (appointmentRows && appointmentRows.length > 1) {
      logAndThrow("Multiple active appointments found for namespaced integration identifier", {
        groupId: group.id,
        namespace,
      });
    }

    return appointmentRows?.[0] ? mapAppointmentRowToAppointment(appointmentRows[0]) : null;
  },
  "getAppointmentByIntegrationIdentifier"
);

export const createAppointment = withDurationMetric(async function createAppointment(
  appointment: Appointment,
  group: Group
): Promise<Appointment> {
  if (appointment.ehrId) {
    const byEhrId = await getAppointmentByEhrId(appointment.ehrId, group);

    if (byEhrId) {
      throw new Error(`Appointment already exists with ehrId: ${appointment.ehrId}`);
    }
  }

  const integrationIdDuplicatesLegacyEhrId =
    appointment.ehrIdNamespace == null && appointment.integrationAppointmentId === appointment.ehrId;

  if (appointment.integrationAppointmentId && appointment.integrationNamespace && !integrationIdDuplicatesLegacyEhrId) {
    const byIntegrationId = await getAppointmentByIntegrationIdentifier(
      appointment.integrationAppointmentId,
      appointment.integrationNamespace,
      group
    );

    if (byIntegrationId) {
      throw new Error(`Appointment already exists for integration namespace: ${appointment.integrationNamespace}`);
    }
  }

  if (appointment.id) {
    const byId = await getAppointment(appointment.id, group);

    if (byId) {
      throw new Error(`Appointment already exists with id: ${appointment.id}`);
    }
  }

  return insertAppointment(appointment, group);
}, "createAppointment");

export const getAllAppointments = withDurationMetric(async function getAllAppointments(
  group: Group
): Promise<Record<string, Appointment>> {
  if (!group) {
    throw new Error("No group provided");
  }

  const { data: appointmentRows, error } = await withRetry("getAllAppointments", async () => {
    const supabaseClient = await getSupabaseClient();

    return await supabaseClient
      .from(APPOINTMENT_TABLE_NAME)
      .select(fullAppointmentSelect)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
  });

  if (error) {
    logAndThrow("Error fetching appointments for group", { error });
  }

  if (!appointmentRows) {
    return {};
  }

  const appointments = appointmentRows.map(mapAppointmentRowToAppointment);

  const appointmentsMap: Record<string, Appointment> = {};

  appointments.forEach((appointment: Appointment) => {
    appointmentsMap[appointment.id] = appointment;
  });

  return appointmentsMap;
}, "getAllAppointments");

export const getAllAppointmentsByPatientIds = withDurationMetric(async function getAllAppointmentsByPatientIds(
  patientIds: string[],
  group: Group
): Promise<Appointment[]> {
  if (!group) {
    throw new Error("No group provided");
  }

  // Return empty array if no patient IDs provided
  if (!patientIds || patientIds.length === 0) {
    return [];
  }

  // Batch queries to avoid HTTP 414 URI Too Long errors
  // Supabase constructs URLs with all IDs in the query string, which can exceed HTTP URI length limits
  const BATCH_SIZE = 50;
  const allAppointments: Appointment[] = [];

  for (let i = 0; i < patientIds.length; i += BATCH_SIZE) {
    const batch = patientIds.slice(i, i + BATCH_SIZE);

    const { data: appointmentRows, error } = await withRetry("getAllAppointmentsByPatientIds", async () => {
      const supabaseClient = await getSupabaseClient();

      return await supabaseClient
        .from(APPOINTMENT_TABLE_NAME)
        .select(fullAppointmentSelect)
        .eq("group_id", group.id)
        .is("deleted_at", null)
        .in("patient_id", batch)
        .order("created_at", { ascending: false });
    });

    if (error) {
      logger.error("Error fetching appointments for patient batch:", {
        batchStart: i,
        batchSize: batch.length,
        totalPatientIds: patientIds.length,
        error,
        errorMessage: error?.message,
        errorCode: error?.code,
        errorDetails: error?.details,
        errorHint: error?.hint,
      });
      throw new Error(`Error fetching appointments: ${error?.message || JSON.stringify(error) || "Unknown error"}`);
    }

    if (appointmentRows) {
      const appointments = appointmentRows.map(mapAppointmentRowToAppointment);

      allAppointments.push(...appointments);
    }
  }

  return allAppointments;
}, "getAllAppointmentsByPatientIds");

export const getAppointmentsByPatientAndIntegration = withDurationMetric(
  async function getAppointmentsByPatientAndIntegration(
    patientId: string,
    namespace: AppointmentIntegrationNamespace,
    group: Group
  ): Promise<Appointment[]> {
    if (!patientId) return [];

    const { data: appointmentRows, error } = await withRetry("getAppointmentsByPatientAndIntegration", async () => {
      const supabaseClient = await getSupabaseClient();

      return supabaseClient
        .from(APPOINTMENT_TABLE_NAME)
        .select(fullAppointmentSelect)
        .eq("group_id", group.id)
        .eq("patient_id", patientId)
        .eq("integration_namespace", namespace)
        .is("deleted_at", null)
        .order("appointment_datetime", { ascending: true, nullsFirst: false });
    });

    if (error) {
      logAndThrow("Error fetching appointments by patient and integration", {
        error,
        groupId: group.id,
        namespace,
        patientId,
      });
    }

    return (appointmentRows ?? []).map(mapAppointmentRowToAppointment);
  },
  "getAppointmentsByPatientAndIntegration"
);

/**
 * Returns true if the patient has at least one appointment whose
 * `appointment_datetime` is in the future. Used by gates that need to know
 * whether a patient is still being actively seen (e.g. PA East's
 * transfer-of-care alert check — VAL-1408). Does not filter on status: a
 * future-dated row in any state still signals "the practice expects them".
 */
export const hasUpcomingAppointment = withDurationMetric(async function hasUpcomingAppointment(
  patientId: string,
  group: Group
): Promise<boolean> {
  if (!patientId) return false;

  const nowIso = new Date().toISOString();

  const supabase = await getSupabaseClient();
  const { count, error } = await supabase
    .from(APPOINTMENT_TABLE_NAME)
    .select("id", { count: "exact", head: true })
    .eq("group_id", group.id)
    .eq("patient_id", patientId)
    .is("deleted_at", null)
    .gt("appointment_datetime", nowIso);

  if (error) {
    logAndThrow("hasUpcomingAppointment failed", { patientId, groupId: group.id, error });
  }

  return (count ?? 0) > 0;
}, "hasUpcomingAppointment");

export const getAppointmentsByReferralId = withDurationMetric(
  async (referralId: string, group: Group): Promise<Appointment[]> => {
    if (!referralId || !group) {
      return [];
    }

    const { data: appointmentRows, error } = await withRetry("getAppointmentsByReferralId", async () => {
      const supabaseClient = await getSupabaseClient();

      return await supabaseClient
        .from(APPOINTMENT_TABLE_NAME)
        .select(`${fullAppointmentSelect}, referral_appointment!inner(referral_id)`)
        .eq("group_id", group.id)
        .eq("referral_appointment.referral_id", referralId)
        .eq("referral_appointment.group_id", group.id)
        .is("deleted_at", null)
        .order("appointment_datetime", { ascending: false, nullsFirst: false });
    });

    if (error) {
      logAndThrow("Error fetching appointments by referral ID:", {
        referralId,
        error,
      });
    }

    if (!appointmentRows) {
      return [];
    }

    return appointmentRows.map(mapAppointmentRowToAppointment);
  },
  "getAppointmentsByReferralId"
);

export const getPaginatedAppointmentsMetadata = withDurationMetric(
  async (params: AppointmentMetadataFiltersParams, group: Group): Promise<PaginatedAppointmentsMetadataResponse> => {
    const { createdBy, page, pageSize, sortBy, sortOrder } = params;
    const offset = page * pageSize;
    const sortField = resolveAppointmentListSortField(sortBy);

    const supabaseClient = await getSupabaseClient();

    let dataQuery = supabaseClient
      .from(APPOINTMENT_TABLE_NAME)
      .select("*, patient:patient_id(given_name, family_name, display_id)", { count: "exact" })
      .eq("group_id", group.id)
      .is("deleted_at", null);

    if (createdBy) {
      dataQuery = dataQuery.in("created_by", createdBy.split(",") as AppointmentCreatedBy[]) as typeof dataQuery;
    }

    const { data, error, count } = await dataQuery
      .order(sortField, { ascending: sortOrder === "asc", nullsFirst: false })
      .order("id", { ascending: true })
      .range(offset, offset + pageSize - 1);

    if (error) {
      throw logAndThrow("Error fetching appointments", { error });
    }

    const appointments: AppointmentMetadata[] = (data ?? []).map((row: any) => ({
      id: row.id,
      displayId: row.display_id,
      groupId: row.group_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      appointmentCreatedAt: row.appointment_created_at,
      appointmentDatetime: row.appointment_datetime,
      createdBy: row.created_by,
      type: row.type,
      status: row.status,
      ehrId: row.ehr_id,
      ehrReason: row.ehr_reason,
      ehrStatus: row.ehr_status,
      ehrType: row.ehr_type,
      patientId: row.patient_id,
      patientGivenName: row.patient?.given_name ?? null,
      patientFamilyName: row.patient?.family_name ?? null,
      patientDisplayId: row.patient?.display_id ?? null,
    }));

    return { appointments, totalCount: count ?? 0 };
  },
  "getPaginatedAppointmentsMetadata"
);

type AppointmentMetadataExportFiltersParams = Omit<AppointmentMetadataFiltersParams, "page" | "pageSize">;

export async function getExportAppointmentsMetadata(
  params: AppointmentMetadataExportFiltersParams,
  group: Group
): Promise<PaginatedAppointmentsMetadataResponse> {
  return getPaginatedAppointmentsMetadata({ ...params, page: 0, pageSize: CSV_EXPORT_MAX_ROWS }, group);
}
